import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileSignature, Copy, Check, Clock, ChevronDown,
} from 'lucide-react';
import axios from 'axios';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { persistProfilePref } from '../../utils/persistProfilePref';

// ── CPT Code Constants ──

export const TIME_CPT_MAP = {
  '20': { code: '99213', desc: 'Low-Moderate Complexity' },
  '30': { code: '99214', desc: 'Moderate Complexity' },
  '40': { code: '99214', desc: 'Moderate Complexity' },
  '50': { code: '99215', desc: 'High Complexity' },
};

export const ALL_CPT_CODES = [
  { code: '99213', label: 'E/M Low-Moderate', group: 'E/M' },
  { code: '99214', label: 'E/M Moderate', group: 'E/M' },
  { code: '99215', label: 'E/M High', group: 'E/M' },
  { code: '99417', label: 'Extended Time (add-on)', group: 'E/M' },
  { code: '99391', label: 'Preventive <1yr', group: 'Wellness' },
  { code: '99392', label: 'Preventive 1-4yr', group: 'Wellness' },
  { code: '99393', label: 'Preventive 5-11yr', group: 'Wellness' },
  { code: '99394', label: 'Preventive 12-17yr', group: 'Wellness' },
  { code: '99395', label: 'Preventive 18-39yr', group: 'Wellness' },
  { code: '99396', label: 'Preventive 40-64yr', group: 'Wellness' },
  { code: '99397', label: 'Preventive 65+', group: 'Wellness' },
  { code: '99201', label: 'New Pt Straightforward', group: 'New Patient' },
  { code: '99202', label: 'New Pt Low', group: 'New Patient' },
  { code: '99203', label: 'New Pt Moderate', group: 'New Patient' },
  { code: '99204', label: 'New Pt Mod-High', group: 'New Patient' },
  { code: '99205', label: 'New Pt High', group: 'New Patient' },
  { code: '2000F', label: 'BP Measured', group: 'Quality' },
  { code: '2001F', label: 'Weight Screen & Follow-Up', group: 'Quality' },
  { code: '3008F', label: 'BMI Documented', group: 'Quality' },
  { code: 'Q0091', label: 'PAP Smear Collection', group: 'Procedures' },
  { code: '58300', label: 'IUD Insertion', group: 'Procedures' },
  { code: '58301', label: 'IUD Removal', group: 'Procedures' },
  { code: '11981', label: 'Implant Insertion', group: 'Procedures' },
  { code: '11982', label: 'Implant Removal', group: 'Procedures' },
  { code: '11983', label: 'Implant Remove + Reinsert', group: 'Procedures' },
  { code: '96160', label: 'Health Risk Assessment (PHQ/GAD)', group: 'Screening' },
  { code: '96127', label: 'Emotional/BH Screening (PHQ-9/GAD-7)', group: 'Screening' },
];

export const VIRTUAL_CPT_ESTABLISHED = [
  { code: '98004', label: 'A/V Straightforward MDM or 10 min', time: '10' },
  { code: '98005', label: 'A/V Low MDM or 20 min', time: '20' },
  { code: '98006', label: 'A/V Moderate MDM or 30 min', time: '30' },
  { code: '98007', label: 'A/V High MDM or 40 min', time: '40' },
  { code: '98012', label: 'Aud Only Straightforward MDM or 10 min', time: '10' },
  { code: '98013', label: 'Aud Only Low MDM or 20 min', time: '20' },
  { code: '98014', label: 'Aud Only Moderate MDM or 30 min', time: '30' },
  { code: '98015', label: 'Aud Only High MDM or 40 min', time: '40' },
];

export const VIRTUAL_CPT_NEW = [
  { code: '98000', label: 'A/V New Pt Straightforward MDM', time: '10' },
  { code: '98001', label: 'A/V New Pt Low MDM', time: '20' },
  { code: '98002', label: 'A/V New Pt Moderate MDM', time: '30' },
  { code: '98003', label: 'A/V New Pt High MDM', time: '40' },
  { code: '98008', label: 'Aud New Pt Straightforward MDM', time: '10' },
  { code: '98009', label: 'Aud New Pt Low MDM', time: '20' },
  { code: '98010', label: 'Aud New Pt Moderate MDM', time: '30' },
  { code: '98011', label: 'Aud New Pt High MDM', time: '40' },
];

// Stable CopyButton for signature block
const CopyButtonLocal = ({ text, field, label = "Copy", handleCopy, copiedField, prominent = false }) => (
  <Button variant={prominent ? "secondary" : "ghost"} size="sm" onClick={() => handleCopy(text, field)}
    className={`${prominent ? 'h-9 px-4 bg-primary/15 hover:bg-primary/25 border border-primary/30 text-primary font-semibold shadow-sm' : 'h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/10'} ${copiedField === field ? 'copy-success' : ''}`}
    data-testid={`copy-${field}-button`}
    title={`Copy ${label.toLowerCase()}`}>
    {copiedField === field ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
    {copiedField === field ? 'Copied!' : label}
  </Button>
);

const NoteSignatureBlock = ({ providerSignatureBlock, patientLabel, handleCopy, copiedField, result, noteTitle, visitType, setVisitType, encounterType, getFilledLetter, setResult, onFullBlockChange, virtualModality, setVirtualModality, dosOverride, setDosOverride, dosTimeOverride, setDosTimeOverride, lateEntry, setLateEntry }) => {
  const [minutes, setMinutes] = useState(() => localStorage.getItem('writeright_sign_minutes') || '40');
  const [timePhrase, setTimePhrase] = useState(() => localStorage.getItem('writeright_sign_phrase') || 'greater_than');
  const [noteCompleted] = useState(() => new Date());
  const [virtualCptCode, setVirtualCptCode] = useState(() => localStorage.getItem('writeright_virtual_cpt') || '98006');
  
  const [selectedCpts, setSelectedCpts] = useState(() => {
    const saved = localStorage.getItem('writeright_selected_cpts');
    return saved ? JSON.parse(saved) : [];
  });
  // CPT picker: collapsed by default (Feb 24, 2026 user pref — sleek look).
  // Persists user's expand/collapse choice so they don't have to re-toggle
  // every encounter once they decide.
  const [showCptPicker, setShowCptPicker] = useState(() => {
    try { return localStorage.getItem('writeright_cpt_picker_open') === 'true'; }
    catch { return false; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('writeright_cpt_picker_open', String(showCptPicker)); }
    catch { /* ignore */ }
  }, [showCptPicker]);
  
  // Favorite CPT Combos (API-backed)
  const [favoriteCptCombos, setFavoriteCptCombos] = useState([]);
  const [showSaveCombo, setShowSaveCombo] = useState(false);
  const [newComboName, setNewComboName] = useState('');
  const [comboModality, setComboModality] = useState('');
  const [combosLoaded, setCombosLoaded] = useState(false);

  useEffect(() => {
    const loadCombos = async () => {
      try {
        const gid = localStorage.getItem('writeright_guest_id') || '';
        const res = await axios.get(`${window.__WR_API_BASE__}/api/cpt-combos?guest_id=${gid}`, { withCredentials: true });
        setFavoriteCptCombos(res.data.combos || []);
        setCombosLoaded(true);
      } catch (e) {
        const saved = localStorage.getItem('writeright_fav_cpt_combos');
        if (saved) setFavoriteCptCombos(JSON.parse(saved));
        setCombosLoaded(true);
      }
    };
    loadCombos();
  }, []);
  
  // dosOverride and dosTimeOverride are now props from App.js

  // Auto-select 96127 when any F-code (behavioral health) is detected in ICD-10
  React.useEffect(() => {
    if (!result) return;
    const codes = (result.icd10_suggestions || []).map(c => typeof c === 'object' ? c.code : c);
    const hasFCode = codes.some(code => /^F\d/.test(code));
    if (hasFCode) {
      const already = selectedCpts.some(c => c.code === '96127');
      if (!already) {
        const screeningCode = ALL_CPT_CODES.find(c => c.code === '96127');
        if (screeningCode) {
          const updated = [...selectedCpts, screeningCode];
          setSelectedCpts(updated);
          localStorage.setItem('writeright_selected_cpts', JSON.stringify(updated));
        }
      }
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps


  React.useEffect(() => {
    // Mar 2, 2026 — Previously wiped dosOverride + dosTimeOverride here
    // whenever `result` transitioned to falsy. That fired during Regenerate
    // (SSE streams / interim state changes) and destroyed the back-charted
    // date the provider had explicitly set. DOS is a PATIENT-level field —
    // clearAll / Next Patient already resets it via useFormActions, so this
    // side-effect was redundant AND destructive. Now we ONLY reset the CPT
    // selections here (the actual scoped concern of this effect).
    // Reset CPT selections from localStorage when result changes (e.g., loading from history)
    const stored = localStorage.getItem('writeright_selected_cpts');
    if (!stored || stored === '[]') {
      setSelectedCpts([]);
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for explicit "clear CPTs" events from clearAll/nextPatient — guarantees
  // the React state matches localStorage even when result hasn't changed yet.
  React.useEffect(() => {
    const handler = () => setSelectedCpts([]);
    window.addEventListener('writeright:clear-cpts', handler);
    return () => window.removeEventListener('writeright:clear-cpts', handler);
  }, []);

  const isVirtual = visitType === 'virtual' || visitType === 'results_review_virtual';
  const isNewPatient = encounterType === 'new';
  const virtualCptOptions = isNewPatient ? VIRTUAL_CPT_NEW : VIRTUAL_CPT_ESTABLISHED;
  const selectedVirtualCpt = virtualCptOptions.find(c => c.code === virtualCptCode) || virtualCptOptions[2];
  const autoCpt = isVirtual ? selectedVirtualCpt : (TIME_CPT_MAP[minutes] || TIME_CPT_MAP['40']);

  const activeCptCodes = useMemo(() => {
    if (selectedCpts.length > 0) return selectedCpts.map(c => c.code).join(', ');
    return autoCpt.code;
  }, [selectedCpts, autoCpt]);

  const activeCptLabel = useMemo(() => {
    if (selectedCpts.length > 0) return selectedCpts.map(c => `${c.code} ${c.label || ''}`).join(' + ');
    return `${autoCpt.code} ${autoCpt.label || autoCpt.desc || ''}`;
  }, [selectedCpts, autoCpt]);

  /**
   * Heuristic MDM-tier suggestion for VIRTUAL encounters.
   * Scans the encounter result + clinical note text for complexity signals:
   *   - chronic conditions count (ICD-10 codes)
   *   - behavioral health (F-codes add weight)
   *   - medication changes (start/stop/increase/decrease)
   *   - referrals + imaging + labs orders
   *   - return precautions / red flags
   * Maps total score to one of 4 tiers (index 0-3 = SF / Low / Mod / High).
   * Returns null when no clinical note is present yet.
   */
  const suggestedMdmTier = useMemo(() => {
    try {
      if (!isVirtual || !result || typeof result.clinical_note !== 'string' || !result.clinical_note) return null;
      const note = result.clinical_note.toLowerCase();
      const rawCodes = Array.isArray(result.icd10_suggestions) ? result.icd10_suggestions : [];
      const codes = rawCodes.map(c => (c && typeof c === 'object') ? (c.code || '') : (typeof c === 'string' ? c : '')).filter(Boolean);
      const icd10Count = codes.length;
      const hasFCode = codes.some(c => /^F\d/.test(c));
      const refsArr = Array.isArray(result.referrals) ? result.referrals : [];
      const imgsArr = Array.isArray(result.imaging_orders) ? result.imaging_orders : [];
      const refCount = refsArr.length + (result.indication ? 1 : 0);
      const imgCount = imgsArr.length;
      const hasLabs = !!result.lab_orders;
      // Medication management — common verbs that signal Rx decision-making
      const medChanges = (note.match(/\b(increase[d]?|decrease[d]?|started?|stopped?|discontinue[d]?|titrate[d]?|adjust(?:ed)?\s+(?:dose|medication)|new\s+prescription|refill[ed]?|switch(?:ed)?\s+to)\b/g) || []).length;
      const hasReturnPrecaution = /return\s+(?:if|to|precaution)|come\s+back\s+if|seek\s+(?:emergency|er|ed)|call\s+(?:911|provider)/i.test(note);
      const hasRedFlag = /\b(severe|acute|stat|urgent|emergency|hospitaliz|chest\s+pain|shortness\s+of\s+breath|syncope|suicidal|homicidal|severe\s+pain)\b/i.test(note);
      const hasExacerbation = /\bexacerbat|flare|worsening|uncontrolled\b/i.test(note);
      const planLength = (result.clinical_note.match(/^[-•]/gm) || []).length;
      // Scoring
      let score = 0;
      score += Math.min(icd10Count, 5) * 1.0;
      score += hasFCode ? 1.5 : 0;
      score += refCount * 1.5;
      score += imgCount * 1.0;
      score += hasLabs ? 1.0 : 0;
      score += Math.min(medChanges, 4) * 0.75;
      score += hasReturnPrecaution ? 1.0 : 0;
      score += hasRedFlag ? 2.5 : 0;
      score += hasExacerbation ? 1.5 : 0;
      score += Math.min(planLength, 6) * 0.3;
      // Map score → tier index (0=SF, 1=Low, 2=Mod, 3=High)
      let tierIdx;
      if (score <= 2.5) tierIdx = 0;
      else if (score <= 4.5) tierIdx = 1;
      else if (score <= 7.5) tierIdx = 2;
      else tierIdx = 3;
      // Build reason string for the tooltip
      const reasons = [];
      if (icd10Count > 0) reasons.push(`${icd10Count} dx`);
      if (hasFCode) reasons.push('behavioral health');
      if (refCount > 0) reasons.push(`${refCount} referral${refCount === 1 ? '' : 's'}`);
      if (imgCount > 0) reasons.push(`${imgCount} imaging`);
      if (hasLabs) reasons.push('labs');
      if (medChanges > 0) reasons.push(`${medChanges} med change${medChanges === 1 ? '' : 's'}`);
      if (hasReturnPrecaution) reasons.push('return precautions');
      if (hasRedFlag) reasons.push('red flag');
      if (hasExacerbation) reasons.push('exacerbation');
      return { tierIdx, score: Math.round(score * 10) / 10, reasons };
    } catch (e) {
      // Never crash the signature block over a heuristic — fail silent
      console.warn('suggestedMdmTier heuristic failed:', e);
      return null;
    }
  }, [isVirtual, result]);

  const dateOfService = useMemo(() => {
    if (dosOverride) {
      const [year, month, day] = dosOverride.split('-').map(Number);
      if (dosTimeOverride) {
        const [h, m] = dosTimeOverride.split(':').map(Number);
        return new Date(year, month - 1, day, h, m, 0);
      }
      const now = new Date();
      if (patientLabel) {
        const timeMatch = patientLabel.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (timeMatch) {
          let h = parseInt(timeMatch[1]);
          const min = parseInt(timeMatch[2]);
          const period = timeMatch[3].toUpperCase();
          if (period === 'PM' && h !== 12) h += 12;
          if (period === 'AM' && h === 12) h = 0;
          return new Date(year, month - 1, day, h, min, 0);
        }
      }
      return new Date(year, month - 1, day, now.getHours(), now.getMinutes(), 0);
    }
    const now = new Date();
    if (patientLabel) {
      const timeMatch = patientLabel.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (timeMatch) {
        let h = parseInt(timeMatch[1]);
        const m = parseInt(timeMatch[2]);
        const period = timeMatch[3].toUpperCase();
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);
      }
    }
    return new Date(now.getTime() - parseInt(minutes) * 60000);
  }, [patientLabel, minutes, dosOverride, dosTimeOverride]);

  const formatTS = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    // Mar 2, 2026 — Provider requested military 24-hour time format
    // `HHMM hrs` (no colon, no seconds, no AM/PM). Matches AR 25-50 /
    // standard military charting convention. Was `HH:MM:SS`.
    return `${mm}/${dd}/${yy} ${hh}${min} hrs`;
  };

  const phraseText = timePhrase === 'greater_than' ? 'greater than' : 'approximately';
  
  const cptAttestationText = selectedCpts.length > 0
    ? `CPT Code${selectedCpts.length > 1 ? 's' : ''} ${selectedCpts.map(c => c.code).join(', ')}`
    : `CPT Code ${autoCpt.code}${autoCpt.label ? ' ' + autoCpt.label : ''}`;

  const modalityText = virtualModality === 'telephone' ? 'telephonically' : 'via video';
  const virtualAttestation = `This visit was completed ${modalityText}. All issues discussed and addressed but no physical exam was performed. ${selectedVirtualCpt.time || minutes} minutes were spent on digital evaluation and management of this patient, including visit hx, specialists progress notes, med reconciliation, and med renewals. I spent ${phraseText} 50% of the visit counseling or coordinating care.\n${cptAttestationText}`;
  const standardAttestation = `I spent a total of ${minutes} minutes in the care of this patient. This includes review of past records, assessment and plan of care. All questions were answered and patient agrees with plan. I spent ${phraseText} 50% of the visit counseling or coordinating care. ${cptAttestationText}`;
  const dragonDisclaimer = `\nNote: Portions of this note were made using Dragon Medical voice recognition software. Grammar errors, random word insertions, pronunciation errors and incomplete sentences are an occasional consequence of this system. Any formal questions or concerns about the content or information contained within the body of this dictation should be directed to the provider for clarification.`;
  const attestation = isVirtual ? virtualAttestation : standardAttestation;

  // v6.8.48 — Late-entry stamp. When the provider has flipped the
  // intake-bar checkbox, prepend a "*** LATE ENTRY ***" header and
  // append the marker to the Note Updated line so the EMR signature
  // audit trail captures it verbatim.
  const lateEntryHeader = lateEntry ? '*** LATE ENTRY ***\n\n' : '';
  const lateEntrySuffix = lateEntry ? '   *** LATE ENTRY ***' : '';

  const fullBlock = isVirtual ? [
    lateEntryHeader + attestation, dragonDisclaimer, '',
    providerSignatureBlock || '________________________________\n(Provider Signature Block)', '',
    `Date of Service: ${formatTS(dateOfService)}   .appt${lateEntrySuffix}`, '',
    `Note Completed:  ${formatTS(noteCompleted)}`, '',
    `Note Updated:  ${formatTS(noteCompleted)}${lateEntry ? '   *** LATE ENTRY ***' : ' ---Late entry if applicable'}`,
  ].join('\n') : [
    lateEntryHeader + attestation, '',
    providerSignatureBlock || '________________________________\n(Provider Signature Block)', '',
    `Date of Service: ${formatTS(dateOfService)}${lateEntrySuffix}`,
    `Note Completed:  ${formatTS(noteCompleted)}${lateEntrySuffix}`,
  ].join('\n');

  useEffect(() => {
    if (onFullBlockChange) onFullBlockChange(fullBlock);
  }, [fullBlock, onFullBlockChange]);

  const handleMinutesChange = (val) => { setMinutes(val); localStorage.setItem('writeright_sign_minutes', val); };
  const handlePhraseChange = (val) => { setTimePhrase(val); localStorage.setItem('writeright_sign_phrase', val); };
  const handleVirtualCptChange = (val) => { setVirtualCptCode(val); localStorage.setItem('writeright_virtual_cpt', val); };
  // Map any audio-video CPT to its audio-only equivalent (or vice versa) when
  // the provider toggles the modality. The constants are organized so that
  // audio-only code = audio-video code + 8 (98000↔98008, 98004↔98012, etc.).
  // Bug reported Feb 14, 2026: toggling Telephone left 98006 (A/V) selected.
  const mapCptToModality = React.useCallback((code, modality) => {
    const n = parseInt(code, 10);
    if (Number.isNaN(n) || n < 98000 || n > 98015) return code;
    const isAudioOnly = n >= 98008;
    if (modality === 'telephone' && !isAudioOnly) return String(n + 8);
    if (modality === 'video' && isAudioOnly) return String(n - 8);
    return code;
  }, []);

  // Mount-time consistency check: if the persisted CPT code doesn't match
  // the persisted modality (e.g., 98006 stored but Telephone toggle is on
  // from a previous session), auto-remap to the equivalent. Prevents the
  // bug where the dropdown showed "98006 A/V Moderate" with the Telephone
  // toggle active. MUST be declared AFTER mapCptToModality (TDZ).
  React.useEffect(() => {
    if (!isVirtual) return;
    const mapped = mapCptToModality(virtualCptCode, virtualModality);
    if (mapped !== virtualCptCode) {
      setVirtualCptCode(mapped);
      localStorage.setItem('writeright_virtual_cpt', mapped);
    }
  }, [virtualModality, isVirtual, virtualCptCode, mapCptToModality]);

  const handleModalityChange = (val) => {
    setVirtualModality(val);
    localStorage.setItem('writeright_virtual_modality', val);
    persistProfilePref({ virtual_modality: val });
    // Auto-remap the selected CPT to the equivalent code in the new modality
    const mapped = mapCptToModality(virtualCptCode, val);
    if (mapped !== virtualCptCode) {
      setVirtualCptCode(mapped);
      localStorage.setItem('writeright_virtual_cpt', mapped);
    }
  };

  const toggleCptCode = (cptItem) => {
    setSelectedCpts(prev => {
      const exists = prev.find(c => c.code === cptItem.code);
      const next = exists ? prev.filter(c => c.code !== cptItem.code) : [...prev, cptItem];
      localStorage.setItem('writeright_selected_cpts', JSON.stringify(next));
      return next;
    });
  };
  const clearCptOverride = () => { setSelectedCpts([]); localStorage.removeItem('writeright_selected_cpts'); };

  const saveCptCombo = async () => {
    if (!newComboName.trim() || selectedCpts.length === 0) return;
    try {
      const gid = localStorage.getItem('writeright_guest_id') || '';
      const res = await axios.post(`${window.__WR_API_BASE__}/api/cpt-combos`, {
        name: newComboName.trim(), codes: selectedCpts, modality: comboModality || null, guest_id: gid,
      }, { withCredentials: true });
      setFavoriteCptCombos(prev => [res.data, ...prev]);
      setNewComboName(''); setComboModality(''); setShowSaveCombo(false);
      toast.success(`Saved combo "${res.data.name}"`);
    } catch (e) { toast.error('Failed to save combo'); }
  };

  const applyCptCombo = async (combo) => {
    setSelectedCpts(combo.codes);
    localStorage.setItem('writeright_selected_cpts', JSON.stringify(combo.codes));
    toast.success(`Applied "${combo.name}"`, { duration: 1500 });
    try {
      await axios.post(`${window.__WR_API_BASE__}/api/cpt-combos/${combo.id}/use`, {}, { withCredentials: true });
      setFavoriteCptCombos(prev => prev.map(c => c.id === combo.id ? { ...c, use_count: (c.use_count || 0) + 1 } : c));
    } catch (e) { /* silent */ }
  };

  const deleteCptCombo = async (comboId) => {
    try {
      await axios.delete(`${window.__WR_API_BASE__}/api/cpt-combos/${comboId}`, { withCredentials: true });
      setFavoriteCptCombos(prev => prev.filter(c => c.id !== comboId));
      toast.success('Combo removed');
    } catch (e) { toast.error('Failed to delete combo'); }
  };

  const handleDosChange = (val) => { setDosOverride(val); };
  const handleDosTimeChange = (val) => { setDosTimeOverride(val); };
  const clearDosOverride = () => { setDosOverride(''); setDosTimeOverride(''); };

  return (
    <div className="mt-4 rounded-lg border border-border/60 bg-card/50" data-testid="note-signature-block">
      {/* Top-of-block quick actions — mirror of the bottom-row buttons so
          providers don't have to scroll past the (often long) signature
          block to reach Copy / Save / Next. Reported May 7, 2026 — high
          tempo workflow needs the actions where the eye lands first. */}
      <div className="flex flex-wrap items-center justify-end gap-2 px-3 py-2 border-b border-border/40 bg-secondary/10 rounded-t-lg">
        <CopyButtonLocal text={fullBlock} field="sign_block_top" label="Copy Sig" handleCopy={handleCopy} copiedField={copiedField} />
        <Button variant="default" size="sm" className="h-8 px-3 text-xs gap-1.5"
          onClick={() => {
            const icd10Codes = result?.icd10_suggestions?.length > 0 ? result.icd10_suggestions.map(c => c.code).join(', ') : '';
            const titleLine = noteTitle || 'CLINICAL NOTE';
            const icd10Line = icd10Codes ? `ICD-10: ${icd10Codes}` : '';
            const parts = [titleLine, icd10Line, result?.clinical_note || '', fullBlock].filter(Boolean);
            handleCopy(parts.join('\n\n'), 'note_with_sign_top');
          }}
          data-testid="copy-note-with-sign-top"
          title="Copy Title + ICD-10 + Note + Signature">
          {copiedField === 'note_with_sign_top' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          Note + Sig
        </Button>
      </div>
      {/* Row 1: Header with time/phrase controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-border/40 bg-secondary/20">
        <div className="flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-primary" />
          <span className="text-xs font-semibold text-foreground">{isVirtual ? 'Virtual Signature Block' : 'Signature Block'}</span>
          {setVisitType && (
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5 ml-1" data-testid="sig-visit-type-toggle">
              <button onClick={() => setVisitType(visitType === 'results_review_virtual' ? 'results_review' : 'in_person_exam')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${!isVirtual ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sig-toggle-in-person">In-Person</button>
              <button onClick={() => setVisitType(visitType === 'results_review' ? 'results_review_virtual' : 'virtual')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${isVirtual ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sig-toggle-virtual">Virtual</button>
            </div>
          )}
        </div>
        {isVirtual ? (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
              <button onClick={() => handleModalityChange('video')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${virtualModality === 'video' ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-modality-video">Video</button>
              <button onClick={() => handleModalityChange('telephone')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${virtualModality === 'telephone' ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-modality-telephone">Telephone</button>
            </div>
            <Select value={virtualCptCode} onValueChange={handleVirtualCptChange}>
              <SelectTrigger className="h-7 w-auto min-w-[200px] text-[10px] bg-input border-border" data-testid="virtual-cpt-select">
                <SelectValue placeholder="Select CPT..." />
              </SelectTrigger>
              <SelectContent>
                {virtualModality === 'video' && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px]">{isNewPatient ? 'New Patient' : 'Established'} — Audio/Video</SelectLabel>
                    {virtualCptOptions.filter(c => c.code.startsWith('980') && parseInt(c.code) < 98008).map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-xs">{c.code} {c.label}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {virtualModality === 'telephone' && (
                  <SelectGroup>
                    <SelectLabel className="text-[10px]">{isNewPatient ? 'New Patient' : 'Established'} — Audio Only (Telephone)</SelectLabel>
                    {virtualCptOptions.filter(c => parseInt(c.code) >= 98008).map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-xs">{c.code} {c.label}</SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            {/* Quick time-tier chips — one tap maps to the matching CPT in the
                current modality. Time tiers differ by patient type:
                  • New: 15/30/45/60 min (SF/Low/Mod/High)
                  • Established: 10/20/30/40 min (SF/Low/Mod/High)
                The AI-suggested tier (heuristic from note complexity) shows a
                subtle blue pulse + dot indicator. Provider always has final say. */}
            {(() => {
              try {
                const tiers = isNewPatient
                  ? [{ t: '15', tier: 'SF' }, { t: '30', tier: 'Low' }, { t: '45', tier: 'Mod' }, { t: '60', tier: 'High' }]
                  : [{ t: '10', tier: 'SF' }, { t: '20', tier: 'Low' }, { t: '30', tier: 'Mod' }, { t: '40', tier: 'High' }];
                const opts = Array.isArray(virtualCptOptions) ? virtualCptOptions : [];
                const activeCodes = virtualModality === 'telephone'
                  ? opts.filter(c => c && c.code && parseInt(c.code, 10) >= 98008)
                  : opts.filter(c => c && c.code && parseInt(c.code, 10) < 98008);
                const activeIdx = activeCodes.findIndex(c => c.code === virtualCptCode);
                const suggestedIdx = suggestedMdmTier?.tierIdx;
                return (
                  <div className="flex gap-0.5 bg-secondary rounded-md p-0.5" data-testid="virtual-time-chips">
                    {tiers.map((tier, i) => {
                      const code = activeCodes[i];
                      if (!code) return null;
                      const isActive = activeIdx === i;
                      const isSuggested = suggestedIdx === i && !isActive;
                      const reasonText = suggestedMdmTier?.reasons?.length
                        ? ` · AI suggests: ${suggestedMdmTier.reasons.join(', ')} (score ${suggestedMdmTier.score})`
                        : '';
                      return (
                        <button
                          key={tier.t}
                          onClick={() => handleVirtualCptChange(code.code)}
                          className={`relative px-2 py-1 rounded text-[9px] font-bold transition-all whitespace-nowrap ${
                            isActive
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : isSuggested
                                ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/40 animate-pulse'
                                : 'text-muted-foreground hover:text-foreground'
                          }`}
                          title={isSuggested
                            ? `${code.code} · ${tier.tier} MDM · ${tier.t} min${reasonText}`
                            : `${code.code} · ${tier.tier} MDM · ${tier.t} min`}
                          data-testid={`virtual-time-${tier.t}`}
                        >
                          {tier.t}m
                          {isSuggested && (
                            <span
                              className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-blue-400 ring-1 ring-blue-500/60"
                              aria-label="AI-suggested tier"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              } catch (e) {
                console.warn('Virtual time chip row render failed:', e);
                return null;
              }
            })()}
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
              <button onClick={() => handlePhraseChange('greater_than')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${timePhrase === 'greater_than' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-phrase-greater">greater than</button>
              <button onClick={() => handlePhraseChange('approximately')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${timePhrase === 'approximately' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-phrase-approx">approximately</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-muted-foreground">Time:</span>
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
              {['20', '30', '40', '50'].map(t => (
                <button key={t} onClick={() => handleMinutesChange(t)}
                  className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${minutes === t ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`sign-time-${t}`}>{t}m</button>
              ))}
            </div>
            <span className="text-[10px] text-muted-foreground">|</span>
            <div className="flex gap-0.5 bg-secondary rounded-md p-0.5">
              <button onClick={() => handlePhraseChange('greater_than')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${timePhrase === 'greater_than' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-phrase-greater">greater than</button>
              <button onClick={() => handlePhraseChange('approximately')}
                className={`px-2 py-1 rounded text-[9px] font-medium transition-all whitespace-nowrap ${timePhrase === 'approximately' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sign-phrase-approx">approximately</button>
            </div>
          </div>
        )}
      </div>

      {/* Row 2: CPT Code selector + Date of Service editor */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border/40 bg-secondary/10">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground font-medium">CPT:</span>
          <button
            onClick={() => setShowCptPicker(!showCptPicker)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold transition-all border ${
              selectedCpts.length > 0
                ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
                : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
            }`}
            data-testid="cpt-picker-toggle"
          >
            {selectedCpts.length > 0 ? (
              <span>{selectedCpts.map(c => c.code).join(' + ')}</span>
            ) : (
              <span>{autoCpt.code} (auto)</span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform ${showCptPicker ? 'rotate-180' : ''}`} />
          </button>
          {selectedCpts.length > 0 && (
            <button onClick={clearCptOverride} className="text-[9px] text-red-400 hover:text-red-300 underline" data-testid="cpt-clear">Reset</button>
          )}
        </div>
        <span className="text-border">|</span>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] text-foreground font-medium" data-testid="dos-auto-display">
            DOS: {formatTS(dateOfService)}
          </span>
          {(dosOverride || dosTimeOverride) && (
            <span className="text-[9px] text-amber-400 font-bold">(MANUAL)</span>
          )}
          <button
            onClick={() => {
              if (!dosOverride) {
                const d = dateOfService;
                const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0');
                const hh = String(d.getHours()).padStart(2, '0'); const min = String(d.getMinutes()).padStart(2, '0');
                handleDosChange(`${yyyy}-${mm}-${dd}`);
                handleDosTimeChange(`${hh}:${min}`);
              } else {
                clearDosOverride();
              }
            }}
            className="text-[9px] px-1.5 py-0.5 rounded bg-secondary hover:bg-secondary/80 text-muted-foreground"
            data-testid="dos-edit-btn"
            title={dosOverride ? 'Reset to auto' : 'Edit date/time'}
          >{dosOverride ? 'Auto' : 'Edit'}</button>
          {dosOverride && (
            <>
              <input type="date" value={dosOverride} onChange={(e) => handleDosChange(e.target.value)}
                className="h-6 px-1 rounded text-[10px] bg-input border border-border text-foreground w-[110px]" data-testid="dos-date-input" />
              <input type="time" value={dosTimeOverride} onChange={(e) => handleDosTimeChange(e.target.value)}
                className="h-6 px-1 rounded text-[10px] bg-input border border-border text-foreground w-[80px]" data-testid="dos-time-input" />
            </>
          )}
        </div>
      </div>

      {/* CPT Multi-Select Picker Panel */}
      {showCptPicker && (
        <div className="px-3 py-2 border-b border-border/40 bg-secondary/5" data-testid="cpt-picker-panel">
          {favoriteCptCombos.length > 0 && (
            <div className="mb-3" data-testid="fav-cpt-combos">
              <p className="text-[9px] font-bold text-amber-400/80 uppercase tracking-wider mb-1">
                Savebook <span className="ml-1 text-[8px] font-normal text-muted-foreground/60">({favoriteCptCombos.length})</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {favoriteCptCombos.map(combo => (
                  <div key={combo.id} className="group flex items-center gap-0.5">
                    <button onClick={() => applyCptCombo(combo)}
                      className="px-2.5 py-1.5 rounded text-[10px] font-semibold bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-all flex items-center gap-1"
                      data-testid={`fav-combo-${combo.id}`}
                      title={`${combo.codes.map(c => `${c.code} ${c.label}`).join(' + ')}${combo.modality ? `\nModality: ${combo.modality.replace('_', ' ')}` : ''}${combo.use_count ? `\nUsed ${combo.use_count}x` : ''}`}>
                      {combo.name}
                      <span className="text-[8px] opacity-50">{combo.codes.map(c => c.code).join('+')}</span>
                      {combo.modality && (
                        <span className="text-[7px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/20">
                          {combo.modality === 'in_person' ? 'IP' : combo.modality === 'video' ? 'Vid' : 'Aud'}
                        </span>
                      )}
                      {(combo.use_count || 0) > 2 && <span className="text-[7px] text-amber-500/50">{combo.use_count}x</span>}
                    </button>
                    <button onClick={() => deleteCptCombo(combo.id)}
                      className="text-[9px] text-red-400/50 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity px-0.5"
                      data-testid={`delete-combo-${combo.id}`} title="Remove">x</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground mb-2">Select one or more CPT codes. Selected codes override the auto-suggested code.</p>
          {['Screening', 'E/M', 'Wellness', 'New Patient', 'Quality', 'Procedures'].map(group => (
            <div key={group} className="mb-2">
              <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{group}</p>
              <div className="flex flex-wrap gap-1">
                {ALL_CPT_CODES.filter(c => c.group === group).map(c => {
                  const isSelected = selectedCpts.some(s => s.code === c.code);
                  return (
                    <button key={c.code} onClick={() => toggleCptCode(c)}
                      className={`px-2 py-1 rounded text-[10px] font-semibold transition-all border ${
                        isSelected ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 shadow-sm' : 'bg-secondary/50 border-border/60 text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                      data-testid={`cpt-option-${c.code}`}>
                      {c.code} <span className="ml-1 text-[8px] opacity-70">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <button onClick={() => setShowCptPicker(false)} className="text-[10px] text-primary hover:underline" data-testid="cpt-picker-done">Done</button>
            {selectedCpts.length > 0 && !showSaveCombo && (
              <button onClick={() => setShowSaveCombo(true)} className="text-[10px] text-amber-400 hover:underline font-medium" data-testid="save-cpt-combo-btn">Save to Savebook</button>
            )}
            {showSaveCombo && (
              <div className="flex flex-wrap items-center gap-1.5 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5">
                <input type="text" value={newComboName} onChange={(e) => setNewComboName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveCptCombo()}
                  placeholder="Combo name (e.g. Wellness + Refill)" className="h-5 px-1.5 rounded text-[10px] bg-input border border-amber-500/40 text-foreground w-44"
                  data-testid="combo-name-input" autoFocus />
                <select value={comboModality} onChange={(e) => setComboModality(e.target.value)}
                  className="h-5 px-1 rounded text-[10px] bg-input border border-border text-foreground" data-testid="combo-modality-select">
                  <option value="">No default modality</option>
                  <option value="in_person">In-Person</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                </select>
                <button onClick={saveCptCombo} className="text-[10px] text-green-400 hover:underline font-semibold" data-testid="combo-save-confirm">Save</button>
                <button onClick={() => { setShowSaveCombo(false); setNewComboName(''); setComboModality(''); }} className="text-[10px] text-red-400 hover:underline">Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signature block content */}
      <div className="px-3 py-3 space-y-3 text-xs font-mono leading-relaxed text-foreground/80 whitespace-pre-wrap" data-testid="sign-block-content">
        <p className="text-foreground/70">{attestation}</p>
        {isVirtual && <p className="text-foreground/50 text-[10px] italic">{dragonDisclaimer.trim()}</p>}
        <div className="pt-2 text-foreground/90 font-semibold whitespace-pre-line">
          {providerSignatureBlock || (
            <span className="text-muted-foreground italic">{'________________________________\n(Set your signature block in Profile Settings)'}</span>
          )}
        </div>
        <div className="pt-1 space-y-0.5 text-[11px] text-muted-foreground">
          <p>Date of Service: <span className={`font-semibold ${(dosOverride || dosTimeOverride) ? 'text-amber-400' : 'text-foreground/80'}`}>{formatTS(dateOfService)}{isVirtual ? '   .appt' : ''}</span>
            {(dosOverride || dosTimeOverride) && <span className="ml-2 text-[9px] text-amber-400 font-bold">(MANUAL DOS)</span>}
          </p>
          <p>Note Completed:  <span className="text-foreground/80 font-semibold">{formatTS(noteCompleted)}</span></p>
          {isVirtual && <p>Note Updated:  <span className="text-foreground/80 font-semibold">{formatTS(noteCompleted)} ---Late entry if applicable</span></p>}
        </div>
      </div>

      {/* Copy button */}
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border/40">
        <CopyButtonLocal text={fullBlock} field="sign_block" label="Copy Signature Block" handleCopy={handleCopy} copiedField={copiedField} />
        <Button variant="outline" size="sm" className="h-8 px-3 text-xs"
          onClick={() => {
            const icd10Codes = result?.icd10_suggestions?.length > 0 ? result.icd10_suggestions.map(c => c.code).join(', ') : '';
            const titleLine = noteTitle || 'CLINICAL NOTE';
            const icd10Line = icd10Codes ? `ICD-10: ${icd10Codes}` : '';
            const parts = [titleLine, icd10Line, result?.clinical_note || '', fullBlock].filter(Boolean);
            handleCopy(parts.join('\n\n'), 'note_with_sign');
          }}
          data-testid="copy-note-with-sign">
          {copiedField === 'note_with_sign' ? <Check className="w-3 h-3 mr-1 text-green-500" /> : <Copy className="w-3 h-3 mr-1" />}
          Copy Note + Signature
        </Button>
      </div>
    </div>
  );
};

export default NoteSignatureBlock;
